// eslint-disable-next-line simple-import-sort/imports
import "./init.js";
import { render } from "react-dom";
import { toast } from "react-toastify";
import { Modal } from "@monorepo/components";
import { IS_THIRD_PARTY } from "@monorepo/constants";
import { logoutAction } from "@monorepo/slices/auth";
import i18n from "@monorepo/translations";
import { ErrorCodeEnum, IExtendedSerializedError, IExtraOptions, IResponseError, TArgs } from "@monorepo/types";
import { BaseQueryFn } from "@reduxjs/toolkit/dist/query";
import * as Sentry from "@sentry/browser";
import { Deserializer } from "jsonapi-serializer";
import queryString from "query-string";
import { valueExists } from "@monorepo/helpers";

type THeaders = {
  "content-type": string;
  accept: string;
  authorization: string;
  platform: string;
  "x-remote-user-id"?: string;
};
const desirializer = new Deserializer({ keyForAttribute: "snake_case" });

const handleModalClose = () => {
  let containerDomNode: HTMLElement;
  containerDomNode = document.getElementById("modal-root") || document.createElement("div");
  if (containerDomNode) {
    document.body.removeChild(containerDomNode);
    let newContainerDomNode: HTMLElement = document.createElement("div");
    newContainerDomNode.setAttribute("id", "modal-root");
    document.body.appendChild(newContainerDomNode);
  }
};

export const customBaseQuery =
  <TResult,>({
    baseUrl,
  }: {
    baseUrl: string | undefined;
  }): BaseQueryFn<TArgs, TResult, Array<IResponseError>, IExtraOptions> =>
  async (args, { dispatch, getState }, extraOptions) => {
    const { url, method = "GET", body, params } = args;
    const { authSlice } = getState() as {
      authSlice: { access_token: string; token_type: string; remote_user_id?: string };
    };
    const { access_token: token, token_type: tokenType, remote_user_id: remoteUserId } = authSlice;

    let queryParams;

    const serializeResponse = extraOptions?.serializeResponse ?? true;
    const responseToBuffer = extraOptions?.responseToBuffer ?? false;
    const pdfResponse = extraOptions?.pdfResponse ?? false;

    const headers = {
      "content-type": `application/${responseToBuffer ? "pdf" : "vnd.api+json"}`,
      accept: `application/${responseToBuffer ? "pdf" : "vnd.api+json"}`,
      authorization: `${tokenType} ${token}`,
      platform: "web",
    } as THeaders;

    if (valueExists(remoteUserId)) {
      headers["x-remote-user-id"] = remoteUserId;
    }

    if (params) {
      queryParams = queryString.stringify(params, { arrayFormat: "bracket" });
    }

    let requestURL = new URL(url, `${baseUrl}`).toString();

    if (queryParams) {
      requestURL = `${requestURL.toString()}?${queryParams}`;
    }

    try {
      const response = await fetch(requestURL, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      let result;

      if (responseToBuffer || pdfResponse) {
        result = await response.arrayBuffer();
      } else {
        const text = await response.text();
        result = text.length ? JSON.parse(text) : undefined;
      }

      if (!result) {
        return {
          data: undefined,
        };
      }

      if (response.status === ErrorCodeEnum.SERVICE_UNAVAILABLE) {
        let containerDomNode: HTMLElement;
        containerDomNode = document.getElementById("modal-root") || document.createElement("div");
        if (containerDomNode) {
          if (!containerDomNode.classList.contains("modal-root")) {
            containerDomNode.setAttribute("id", "modal-root");
          }
          document.body.appendChild(containerDomNode);
          render(
            <Modal open={true} onClose={() => handleModalClose()}>
              {result.errors && result.errors.length > 0 ? result.errors[0].title : result.message}
            </Modal>,
            containerDomNode
          );
        }
      }

      if ("exception" in result) {
        // eslint-disable-next-line no-throw-literal
        throw { ...result, result, response };
      }

      // custom API error
      if ("errors" in result) {
        if (response.status === ErrorCodeEnum.UNAUTHORIZED) {
          dispatch(logoutAction());
        }
        const errors: Array<IResponseError> = [];

        result.errors.forEach((error: IResponseError) => {
          errors.push({ ...error, status: String(response.status) });
        });

        return {
          error: errors as Array<IResponseError>,
        };
      }

      if ("error" in result && response.status === ErrorCodeEnum.BAD_REQUEST) {
        return { error: result };
      }

      if (method === "GET" && serializeResponse) {
        return {
          data: (await desirializer.deserialize(result)) as TResult,
        };
      }
      return { data: result };
    } catch (error) {
      const err = error as IExtendedSerializedError;
      Sentry.captureException(err);
      if (err?.message === "Failed to fetch" || err?.message === "NetworkError") {
        toast.error(i18n.t("common.check_connection"));
      } else {
        toast.error(i18n.t(IS_THIRD_PARTY ? "common.generic_system_error_third_party" : "common.generic_system_error"));
      }
      return {
        data: undefined,
      };
    }
  };
